import { DownOutlined } from '@ant-design/icons'
import { Button, Checkbox, Collapse, DatePicker, Flex, Menu, Tag } from 'antd'
import { useCallback, useMemo, useState } from 'react'

import { Select } from 'src/components'
import { useLocales } from 'src/locales'

import {
  MenuItem,
  reportDetailsFiltersData,
  reportTreeSelectData,
  ReportTreeSelectDataType
} from '../data'
import { useStyles } from '../styles'

const { RangePicker } = DatePicker

export const ReportDetailsFilters = () => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [selectedItems, setSelectedItems] = useState<{
    [key: string]: string[]
  }>({})

  const onSelectFilter = (key: string, value: string | string[]) => {
    setSelectedItems((prev) => {
      const currentValues = prev[key] || []
      const newValues = Array.isArray(value) ? value : [value]

      const updatedValues = [...new Set([...currentValues, ...newValues])]

      return { ...prev, [key]: updatedValues }
    })
  }

  const onRemoveTagFilter = (key: string, value: string) => {
    setSelectedItems((prev) => ({
      ...prev,
      [key]: prev[key]?.filter((tagValue) => tagValue !== value) || []
    }))
  }

  const renderMenuItems = useCallback(
    (items: ReportTreeSelectDataType[]): MenuItem[] =>
      items?.map((item) => ({
        label: (
          <Checkbox
            onClick={(e) => {
              e.stopPropagation()
            }}
          >
            {item.label}
          </Checkbox>
        ),
        key: item.key,
        children: item.children ? renderMenuItems(item.children) : undefined
      })),
    [reportTreeSelectData]
  )

  const tags = useMemo(
    () =>
      Object.entries(selectedItems).flatMap(([key, values]) =>
        values.map((value) => ({
          key: `${key}-${value}`,
          label: `${key}: ${value}`,
          keyCategory: key
        }))
      ),
    [selectedItems]
  )

  return (
    <Collapse
      className={styles.filtersCollapse}
      bordered={false}
      expandIconPosition="end"
      expandIcon={({ isActive }) => (
        <Button
          className={styles.filterCollapseButton}
          type="primary"
          icon={<DownOutlined rotate={isActive ? 180 : 0} />}
        />
      )}
      items={[
        {
          key: 'filter',
          label: (
            <Flex
              justify="space-between"
              onClick={(e) => e.stopPropagation()}
              gap={16}
            >
              <Flex gap={16}>
                <Select
                  className={styles.filterTreeSelect}
                  value={t('reports.by_organizational_structure')}
                  dropdownRender={() => (
                    <Menu
                      rootClassName={styles.treeSelectMenu}
                      mode="inline"
                      items={renderMenuItems(reportTreeSelectData)}
                    />
                  )}
                />

                <RangePicker />
              </Flex>

              <Button type="primary">{t('reports.generate_report')}</Button>
            </Flex>
          ),
          children: (
            <Flex vertical gap={16}>
              <Flex gap={16} wrap>
                {reportDetailsFiltersData?.map((filter) => (
                  <Select
                    mode="multiple"
                    key={filter?.id}
                    placeholder={filter?.title}
                    value={selectedItems[filter?.title] || []}
                    maxTagCount={0}
                    tagRender={() => (
                      <div className={styles.selectedValue}>
                        {filter?.title}
                      </div>
                    )}
                    options={filter.values}
                    onDeselect={(value) =>
                      onRemoveTagFilter(filter?.title, value)
                    }
                    onSelect={(value) => onSelectFilter(filter?.title, value)}
                  />
                ))}
              </Flex>

              <Flex gap={8} wrap>
                {tags?.map(({ key, label, keyCategory }) => (
                  <Tag
                    key={key}
                    closable
                    onClose={() =>
                      onRemoveTagFilter(keyCategory, label.split(': ')[1])
                    }
                  >
                    {label}
                  </Tag>
                ))}
              </Flex>
            </Flex>
          )
        }
      ]}
    />
  )
}
